import type { AlgorithmChallenge } from '@code-arcade/engine'

export const mergeSort: AlgorithmChallenge = {
  id: 'merge-sort',
  title: 'Merge Sort',
  level: 2,
  chapter: 'chapter-2',
  mode: 'race-it',
  description:
    'Divide the array in half, sort each half, then merge them back. O(n log n) — the first algorithm that actually scales.',

  realWorld: {
    scenario:
      "Blink.io's feed ranks 50,000 user events by engagement score every time a user opens the app. Dev Lama's bubble sort worked at 50 users. At 50,000 it pegs the CPU for 4 seconds on every load.",
    consequence:
      "Bubble Sort on 50,000 items: ~1.25 billion comparisons. Merge Sort: ~800,000. That's a 1,500× reduction — the difference between a 4-second freeze and a 3ms sort.",
    products: [],
  },

  visualization: {
    type: 'bar-array',
    defaultSize: 16,
    allowCustomInput: false,
  },

  interrupts: [
    {
      atStep: 30,
      character: 'barry-holt',
      mood: 'urgent',
      message: "The servers stopped crying. What did you do?",
    },
  ],

  complexity: { time: 'O(n log n)', space: 'O(n)' },
  xpReward: { oneStar: 150, twoStar: 300, threeStar: 600 },

  keyTakeaway:
    'Merge sort\'s lesson is the pattern, not the algorithm: split a hard problem into independent subproblems, ' +
    'solve each in isolation, combine results. You will use this when sharding a database query across replicas, ' +
    'when parallelizing a batch job across workers, when designing a MapReduce pipeline. The merge step — where ' +
    'independent work comes back together correctly, in order, without losing data — is the hard part of distributed ' +
    'systems. Merge sort is the simplest example of that coordination problem. O(n log n) is just a byproduct of ' +
    'applying divide-and-conquer recursively. The pattern transfers everywhere scale exists.',

  whenToUse: [
    'Large datasets (n > 1,000) where O(n²) is too slow',
    'When you need a stable sort — equal elements keep their original order (e.g., sort search results by score, then by date: stability ensures equal-score results stay date-ordered)',
    'External sort — data that doesn\'t fit in RAM: read sorted chunks from disk, merge them. Databases do this. Hadoop does this.',
    'Distributed aggregation: fan queries out to N shards, collect sorted responses, merge — you are implementing merge sort at the network level',
    'Sorting linked lists — no random access needed, merge is pointer-based',
  ],

  whenNotToUse: [
    'Memory is tight — merge sort needs O(n) auxiliary space for the temp buffer',
    'Small arrays (< 16 elements) — insertion sort beats it on constant factors',
    'Nearly-sorted data where insertion sort\'s O(n) best case dominates',
  ],

  pseudocode: `procedure mergeSort(A, l, r)
  if l ≥ r − 1 then return
  mid ← ⌊(l + r) / 2⌋
  mergeSort(A, l, mid)
  mergeSort(A, mid, r)
  merge(A, l, mid, r)

procedure merge(A, l, mid, r)
  temp ← []
  i ← l,  j ← mid
  while i < mid and j < r
    if A[i] ≤ A[j]: push A[i]; i ← i + 1
    else:           push A[j]; j ← j + 1
  push A[i..mid) then A[j..r)
  copy temp → A[l..r)`,

  codeSnippets: [
    {
      language: 'csharp',
      label: 'C#',
      code: `public static void MergeSort(int[] arr, int l, int r) {
    if (l >= r) return;
    int mid = (l + r) / 2;
    MergeSort(arr, l, mid);
    MergeSort(arr, mid + 1, r);
    Merge(arr, l, mid, r);
}

static void Merge(int[] arr, int l, int mid, int r) {
    int[] temp = new int[r - l + 1];
    int i = l, j = mid + 1, k = 0;
    while (i <= mid && j <= r)
        temp[k++] = arr[i] <= arr[j] ? arr[i++] : arr[j++];
    while (i <= mid) temp[k++] = arr[i++];
    while (j <= r)   temp[k++] = arr[j++];
    for (int m = 0; m < temp.Length; m++) arr[l + m] = temp[m];
}

// Array.sort() IS this algorithm (TimSort = merge sort + insertion sort). You just saw its internals:
Array.Sort(arr);     // introspective sort — TimSort for objects
list.Sort();         // List<T> — same guarantees`,
    },
    {
      language: 'python',
      label: 'Python',
      code: `def merge_sort(arr: list[int]) -> list[int]:
    if len(arr) <= 1:
        return arr
    mid = len(arr) // 2
    left = merge_sort(arr[:mid])
    right = merge_sort(arr[mid:])
    return _merge(left, right)

def _merge(left: list[int], right: list[int]) -> list[int]:
    result, i, j = [], 0, 0
    while i < len(left) and j < len(right):
        if left[i] <= right[j]:
            result.append(left[i]); i += 1
        else:
            result.append(right[j]); j += 1
    return result + left[i:] + right[j:]


# Array.sort() IS this algorithm (TimSort = merge sort + insertion sort). You just saw its internals:
arr.sort()                # TimSort — O(n log n), stable, beats this impl
sorted_arr = sorted(arr)  # same algorithm, returns new list`,
    },
    {
      language: 'javascript',
      label: 'JavaScript',
      code: `function mergeSort(arr) {
  if (arr.length <= 1) return arr;
  const mid = Math.floor(arr.length / 2);
  const left = mergeSort(arr.slice(0, mid));
  const right = mergeSort(arr.slice(mid));
  return merge(left, right);
}

function merge(left, right) {
  const result = [];
  let i = 0, j = 0;
  while (i < left.length && j < right.length)
    result.push(left[i] <= right[j] ? left[i++] : right[j++]);
  return [...result, ...left.slice(i), ...right.slice(j)];
}

// Array.sort() IS this algorithm (TimSort = merge sort + insertion sort). You just saw its internals:
arr.sort((a, b) => a - b); // V8 TimSort — stable since Node 11`,
    },
    {
      language: 'typescript',
      label: 'TypeScript',
      code: `function mergeSort(arr: number[]): number[] {
  if (arr.length <= 1) return arr;
  const mid = Math.floor(arr.length / 2);
  const left = mergeSort(arr.slice(0, mid));
  const right = mergeSort(arr.slice(mid));
  return merge(left, right);
}

function merge(left: number[], right: number[]): number[] {
  const result: number[] = [];
  let i = 0, j = 0;
  while (i < left.length && j < right.length)
    result.push(left[i] <= right[j] ? left[i++] : right[j++]);
  return [...result, ...left.slice(i), ...right.slice(j)];
}

// Array.sort() IS this algorithm (TimSort = merge sort + insertion sort). You just saw its internals:
arr.sort((a, b) => a - b); // V8 TimSort — stable since Node 11`,
    },
    {
      language: 'java',
      label: 'Java',
      code: `public static void mergeSort(int[] arr, int l, int r) {
    if (l >= r) return;
    int mid = (l + r) / 2;
    mergeSort(arr, l, mid);
    mergeSort(arr, mid + 1, r);
    merge(arr, l, mid, r);
}

static void merge(int[] arr, int l, int mid, int r) {
    int[] temp = new int[r - l + 1];
    int i = l, j = mid + 1, k = 0;
    while (i <= mid && j <= r)
        temp[k++] = arr[i] <= arr[j] ? arr[i++] : arr[j++];
    while (i <= mid) temp[k++] = arr[i++];
    while (j <= r)   temp[k++] = arr[j++];
    for (int m = 0; m < temp.length; m++) arr[l + m] = temp[m];
}

// Array.sort() IS this algorithm (TimSort = merge sort + insertion sort). You just saw its internals:
Arrays.sort(arr);                // dual-pivot quicksort + insertion sort
Arrays.sort(objArr, comparator); // TimSort for objects (stable)`,
    },
  ],

  sampleDataset: {
    label: 'Blink.io feed engagement scores — 50,000 user events to sort on open',
    preview: [1842, 3271, 892, 5504, 2197, 4083, 611, 7892, 3344, 2056, 9103, 445],
    atScale: {
      n: 50000,
      leftLabel: 'Bubble Sort',
      leftNote: '~1.25B comparisons',
      rightLabel: 'Merge Sort',
      rightNote: '~800k comparisons',
    },
  },
}
